/**
 * UGC Video Controls
 * Custom play/pause and mute/unmute controls for UGC video players
 */

(function() {
  'use strict';

  function initVideoControls() {
    /** @type {NodeListOf<HTMLElement>} */
    const players = document.querySelectorAll('[data-ugc-video-player]');
    
    players.forEach(function(player) {
      if (player.dataset.controlsInitialized) return;
      player.dataset.controlsInitialized = 'true';
      
      /** @type {HTMLElement|null} */
      const playPauseBtn = player.querySelector('.ugc-video-controls__play-pause');
      /** @type {HTMLElement|null} */
      const muteBtn = player.querySelector('.ugc-video-controls__mute');
      
      if (!playPauseBtn || !muteBtn) return;
      
      // Observer to detect when video is loaded
      const observer = new MutationObserver(function() {
        /** @type {HTMLVideoElement|null} */
        const video = player.querySelector('video');
        if (video) {
          setupVideoControls(video, /** @type {HTMLElement} */ (playPauseBtn), /** @type {HTMLElement} */ (muteBtn), player);
          observer.disconnect();
        }
      });
      
      observer.observe(player, { childList: true, subtree: true });
      
      // Also check if video already exists
      /** @type {HTMLVideoElement|null} */
      const existingVideo = player.querySelector('video');
      if (existingVideo) {
        setupVideoControls(existingVideo, /** @type {HTMLElement} */ (playPauseBtn), /** @type {HTMLElement} */ (muteBtn), player);
        observer.disconnect();
      }
    });
  }
  
  /**
   * @param {HTMLVideoElement} video
   * @param {HTMLElement} playPauseBtn
   * @param {HTMLElement} muteBtn
   * @param {HTMLElement} player
   */
  function setupVideoControls(video, playPauseBtn, muteBtn, player) {
    // Mark as loaded
    player.classList.add('deferred-media--loaded');
    
    // Ensure video has mobile-friendly attributes
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.playsInline = true;
    
    // User clicked to load video, so unmute and ensure it plays
    video.muted = false;
    muteBtn.setAttribute('data-muted', 'false');
    
    // Ensure video starts playing with sound
    video.play().then(function() {
      playPauseBtn.setAttribute('data-playing', 'true');
    }).catch(function() {
      // If unmuted autoplay fails, try muted (browser policy)
      video.muted = true;
      muteBtn.setAttribute('data-muted', 'true');
      video.play().then(function() {
        playPauseBtn.setAttribute('data-playing', 'true');
      }).catch(function() {
        // If all autoplay fails, keep it paused
        playPauseBtn.setAttribute('data-playing', 'false');
      });
    });
    
    // Prevent slideshow from capturing clicks/touches on controls
    /** @type {HTMLElement|null} */
    var controlsContainer = player.querySelector('.ugc-video-controls');
    if (controlsContainer) {
      var container = controlsContainer;
      ['mousedown', 'touchstart', 'pointerdown'].forEach(function(eventType) {
        container.addEventListener(eventType, function(evt) {
          evt.stopPropagation();
          if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
        }, { capture: true, passive: false });
      });
    }
    
    // Play/Pause functionality
    /** @param {Event} evt */
    function togglePlayPause(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
      
      if (video.paused) {
        // Unmute when user clicks play
        video.muted = false;
        muteBtn.setAttribute('data-muted', 'false');
        
        video.play().catch(function() {
          // Autoplay might be blocked, try muted
          video.muted = true;
          muteBtn.setAttribute('data-muted', 'true');
          video.play();
        });
      } else {
        video.pause();
      }
    }
    
    playPauseBtn.addEventListener('click', togglePlayPause, true);
    playPauseBtn.addEventListener('touchend', function(evt) {
      evt.preventDefault();
      togglePlayPause(evt);
    }, { passive: false });
    
    // Mute/Unmute functionality
    /** @param {Event} evt */
    function toggleMute(evt) {
      evt.preventDefault();
      evt.stopPropagation();
      if (evt.stopImmediatePropagation) evt.stopImmediatePropagation();
      
      video.muted = !video.muted;
      muteBtn.setAttribute('data-muted', video.muted ? 'true' : 'false');
    }
    
    muteBtn.addEventListener('click', toggleMute, true);
    muteBtn.addEventListener('touchend', function(e) {
      e.preventDefault();
      toggleMute(e);
    }, { passive: false });
    
    // Sync button states with video events
    video.addEventListener('play', function() {
      playPauseBtn.setAttribute('data-playing', 'true');
    });
    
    video.addEventListener('pause', function() {
      playPauseBtn.setAttribute('data-playing', 'false');
    });
    
    video.addEventListener('volumechange', function() {
      muteBtn.setAttribute('data-muted', video.muted ? 'true' : 'false');
    });
    
    // Set initial states
    playPauseBtn.setAttribute('data-playing', !video.paused ? 'true' : 'false');
    muteBtn.setAttribute('data-muted', video.muted ? 'true' : 'false');
  }
  
  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVideoControls);
  } else {
    initVideoControls();
  }
  
  // Re-initialize when new content is loaded (for dynamic content)
  document.addEventListener('shopify:section:load', initVideoControls);
  
  // Observe for dynamically added players
  const bodyObserver = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.addedNodes.length) {
        initVideoControls();
      }
    });
  });
  
  bodyObserver.observe(document.body, { childList: true, subtree: true });
})();

